import React, { useState, useCallback, useEffect } from 'react'
import { observer } from 'mobx-react'
import { useLocation, useNavigate } from 'react-router'

import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { DeviceTokenService } from '@evertel/session/DeviceTokenService'
import { LoginService } from '@evertel/session/LoginService'
import { AuthenticationController, RegistrationController } from '@evertel/shared/feature-authentication-auth0'
import { Col, Row, useUI } from '@evertel/web/ui'
import { Step1Register } from './steps/Step1Register'
import { Step2EmailVerification } from './steps/Step2EmailVerification'

// Types
export interface RegistrationData {
    firstName: string
    lastName: string
    email: string
    password: string
    userId?: number
    isGovernmentEmail?: any
}

interface StepConfig {
    title: string
    subtitle: string
    component: React.ComponentType<any>
}

const STEP_CONFIG: Record<number, StepConfig> = {
    1: {
        title: 'Welcome to Evertel',
        subtitle: 'Create your account to get started',
        component: Step1Register
    },
    2: {
        title: 'Verify Your Email Address',
        subtitle: 'Check your email and click on the verification link to continue',
        component: Step2EmailVerification
    }
}

const RegistrationStepperUnauthorized: React.FC = observer(() => {

    const sessionService = useService(SessionService, [])
    const loginService = useService(LoginService, [])
    const deviceTokenService = useService(DeviceTokenService)
    const authenticationController = useService(AuthenticationController)
    const registrationController = useService(RegistrationController)

    const location = useLocation()
    const navigate = useNavigate()

    const [currentStep, setCurrentStep] = useState(1)
    const [registrationData, setRegistrationData] = useState<RegistrationData>({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        userId: -1
    })
    const [isLoading, setIsLoading] = useState(false)

    const { addToast } = useUI()

    // Check for crisis parameter from URL
    const isCrisis = new URLSearchParams(location.search).get('crisis') === 'true'

    const updateRegistrationData = useCallback((field: keyof RegistrationData, value: string) => {
        setRegistrationData(prev => ({
            ...prev,
            [field]: value
        }))
    }, [])

    useEffect(() => {
        if (sessionService.currentUserId) {
            //user is already logged in, go to landing
            //only check this once on page load so it doesn't triger at final registration step
            navigate('/landing', { replace: true })
        }
    }, [])

    const handleContinue = useCallback(async () => {
        if (currentStep === 1) {
            //Register in Auth0
            setIsLoading(true)

            try {
                if (!deviceTokenService.token) {
                    // Try to initialize the device token if it's not available
                    await deviceTokenService.init()
                    if (!deviceTokenService.token) {
                        throw new Error('Device token not available')
                    }
                }

                const emailTrim = registrationData.email?.trim()
                const passwordTrim = registrationData.password?.trim()
                const firstNameTrim = registrationData.firstName?.trim()
                const lastNameTrim = registrationData.lastName?.trim()

                const { id } = await registrationController.registerInAuth0({
                    email: emailTrim,
                    password: passwordTrim,
                    firstName: firstNameTrim,
                    lastName: lastNameTrim,
                    deviceToken: deviceTokenService.token
                })

                updateRegistrationData('userId', id)

                setCurrentStep(2)

            } catch (error: any) {
                addToast({
                    title: 'Error',
                    color: 'danger',
                    message: error.message
                })
            } finally {
                setIsLoading(false)
            }
        } else if (currentStep === 2) {
            setIsLoading(true)

            //logs you in and navigate to SMSAuthentication
            try {
                const emailTrim = registrationData.email?.trim()
                const passwordTrim = registrationData.password?.trim()

                if (isCrisis) {
                    //must be set before login or will be processed too soon.
                    localStorage.setItem('crisis', 'true')
                }

                await loginService.loginWithPassword(emailTrim, passwordTrim)

                loginService.setStartupActionsDisabled(true)
                sessionService.setCallbackUrl(null) // Clear any pre-login callback URL during registration
                loginService.setOnLoginCallback(() => {
                    loginService.setStartupActionsDisabled(true)
                    // navigationService.navigateAuth(SN.REGISTRATION_CONTINUATION, {
                    //     registrationData: {
                    //         email: registrationData.email,
                    //         password: registrationData.password,
                    //         isCrisis: isCrisis
                    //     }
                    // })
                })

                if (isCrisis) {
                    if (registrationData.isGovernmentEmail) {
                        navigate('/landing')
                    } else {
                        navigate('/register/crisisnoaccess')
                    }
                    return
                }

                navigate('/register-sms', {
                    state: {
                        registrationData
                    }
                })

            } catch (error: any) {
                addToast({
                    title: 'Error',
                    color: 'danger',
                    message: error.message
                })

                navigate('/login')
            } finally {
                setIsLoading(false)
            }
        } else if (currentStep === 3) {
            navigate('registration-auth')
        }

    }, [currentStep, registrationData, isCrisis, deviceTokenService, registrationController])

    const handleChangeEmail = async (email: string) => {
        const { userId } = registrationData

        await authenticationController.changeEmailRegistration(userId, {email})
        //only update this after change to be sure it happened
        updateRegistrationData('email', email)
    }

    const renderStepContent = () => {
        const stepConfig = STEP_CONFIG[currentStep]
        if (!stepConfig) return null

        const StepComponent = stepConfig.component

        const commonProps = {
            registrationData,
            updateRegistrationData,
            onStepComplete: handleContinue,
            isCrisis,
            isLoading
        }

        //specific props per step
        const stepSpecificProps = {
            2: {
                isRegistration: true,
                onEmailUpdate: async (newEmail: string) => {
                    await handleChangeEmail(newEmail)
                }
            }
        }

        const finalProps = {
            ...commonProps,
            ...stepSpecificProps[currentStep]
        }

        return <StepComponent {...finalProps} />
    }

    const stepConfig = STEP_CONFIG[currentStep]
    if (!stepConfig) return null

    return (
        <Row
            className="auth-layout animated"
        >
            <Col valign="center" align="center"
                className="p-md-5 p-3"
                style={{ minHeight: '100vh', margin: 'auto', overflow: 'auto', maxWidth: 900 }}
            >
                {renderStepContent()}
            </Col>
        </Row>
    )
})

export { RegistrationStepperUnauthorized }
